import { useTerminalsStateRE } from "@/States/Terminals";
import { useTrans } from "@/translations";

import { useContext, useMemo } from "react";

import { twMerge } from "tailwind-merge";

import { ViewType } from "../../../constants";
import { RecyclingRateTrendContext } from "../../../context";

const OverlayLineSwatch: React.FC<{ color: string; active: boolean }> = ({
  color,
  active,
}) => (
  <span
    className="inline-block h-0 w-3 flex-none"
    style={{ borderTop: `2.0625px solid ${active ? color : "#C9C1C1"}` }}
  />
);

const OverlayBarSwatch: React.FC<{ active: boolean }> = ({ active }) => (
  <span className="inline-flex flex-none items-end gap-[2px]">
    <span
      className="w-[5px] rounded-[1.1px]"
      style={{ height: 10, background: active ? "#9ab8e4" : "#C9C1C1" }}
    />
    <span
      className="w-[5px] rounded-[1.1px]"
      style={{ height: 14, background: active ? "#467432" : "#C9C1C1" }}
    />
  </span>
);

const OverlayButton: React.FC<{
  label: string;
  active: boolean;
  onClick: () => void;
  indicator: React.ReactNode;
}> = ({ label, active, onClick, indicator }) => (
  <button
    onClick={onClick}
    className={twMerge(
      "flex cursor-pointer items-center gap-[7px] rounded-md border px-[11px] py-[5px] text-[12px]",
      active
        ? "border-grey4 bg-grey1 font-semibold text-black"
        : "border-grey2 text-grey5 font-normal",
    )}
  >
    {indicator}
    {label}
  </button>
);

export const ComparisonBenchmarks: React.FC = () => {
  const { t } = useTrans();
  const {
    target,
    showTarget,
    setShowTarget,
    showCompareYear,
    setShowCompareYear,
    showPortfolioAverage,
    setShowPortfolioAverage,
    viewType,
  } = useContext(RecyclingRateTrendContext);

  const { terminals } = useTerminalsStateRE();

  const isBuildingViewType = useMemo(
    () => viewType === ViewType.BUILDING,
    [viewType],
  );

  return (
    <div className="border-grey2 border-t p-3">
      <div className="text-grey6 mb-[9px] text-[10.5px] font-bold tracking-[0.05] uppercase">
        {t("homePage:optionsBenchmarkTitle")}
      </div>
      <div className="flex flex-wrap gap-[4.95px]">
        <OverlayButton
          label={t("homePage:recyclingRateTarget", {
            targetValue: target,
          })}
          active={showTarget}
          onClick={() => setShowTarget(!showTarget)}
          indicator={<OverlayLineSwatch color="#467432" active={showTarget} />}
        />
        {isBuildingViewType && terminals?.length > 1 && (
          <OverlayButton
            label={t("homePage:portfolioAverage")}
            active={showPortfolioAverage}
            onClick={() => setShowPortfolioAverage(!showPortfolioAverage)}
            indicator={
              <OverlayLineSwatch
                color="#B73A23"
                active={showPortfolioAverage}
              />
            }
          />
        )}
        <OverlayButton
          label={t("homePage:optionsBenchmarkYearBefore")}
          active={showCompareYear}
          onClick={() => setShowCompareYear(!showCompareYear)}
          indicator={<OverlayBarSwatch active={showCompareYear} />}
        />
      </div>
    </div>
  );
};
